// frontend/src/pages/OnboardingPage/components/IntegrationCategoryContent.tsx
import { ArrowLeft } from "lucide-react";
import React, { Suspense, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { Navigate, useNavigate } from "react-router";
import {
  getIntegrationCategories,
  INTEGRATION_INFO,
  IntegrationCategory,
  IntegrationType,
} from "src/components/integrations/integration-categories";
import IntegrationCard from "src/components/integrations/IntegrationCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { usePermissions } from "src/context/MeContext";
// Import integration dialogs
import AwsIntegrationsDialogLazy from "src/pages/SettingsPage/components/AwsIntegrationsDialog-lazy";
import BitbucketIntegration from "src/pages/SettingsPage/components/BitbucketIntegration";
import BitbucketIntegrationsDialog from "src/pages/SettingsPage/components/BitbucketIntegrationsDialog";
import BuildkiteIntegration from "src/pages/SettingsPage/components/BuildkiteIntegration";
import CloudWatchIntegrationLazy from "src/pages/SettingsPage/components/CloudWatchIntegration-lazy";
import CloudWatchIntegrationsDialogLazy from "src/pages/SettingsPage/components/CloudWatchIntegrationsDialog-lazy";
import ConfluenceIntegrationLazy from "src/pages/SettingsPage/components/ConfluenceIntegration-lazy";
import CursorIntegration from "src/pages/SettingsPage/components/CursorIntegration";
import DatadogIntegration from "src/pages/SettingsPage/components/DatadogIntegration";
import DatadogIntegrationsDialog from "src/pages/SettingsPage/components/DatadogIntegrationsDialog";
import DevinIntegration from "src/pages/SettingsPage/components/DevinIntegration";
import DiscordIntegrationLazy from "src/pages/SettingsPage/components/DiscordIntegration-lazy";
import ElasticsearchIntegrationLazy from "src/pages/SettingsPage/components/ElasticsearchIntegration-lazy";
import FireHydrantIntegrationLazy from "src/pages/SettingsPage/components/FireHydrantIntegration-lazy";
import GcpCloudMonitoringIntegration from "src/pages/SettingsPage/components/GcpCloudMonitoringIntegration";
import GcpIntegrationLazy from "src/pages/SettingsPage/components/GcpIntegration-lazy";
import GithubIntegration from "src/pages/SettingsPage/components/GithubIntegration";
import GitHubIntegrationsDialog from "src/pages/SettingsPage/components/GitHubIntegrationsDialog";
import GitLabIntegration from "src/pages/SettingsPage/components/GitLabIntegration";
import GitLabIntegrationsDialog from "src/pages/SettingsPage/components/GitLabIntegrationsDialog";
import GrafanaCloudIntegration from "src/pages/SettingsPage/components/GrafanaCloudIntegration";
import IncidentIoIntegration from "src/pages/SettingsPage/components/IncidentIoIntegration";
import { IntegrationCredentialsDialog } from "src/pages/SettingsPage/components/IntegrationsSection";
import JiraIntegrationLazy from "src/pages/SettingsPage/components/JiraIntegration-lazy";
import LangfuseIntegrationLazy from "src/pages/SettingsPage/components/LangfuseIntegration-lazy";
import LangSmithIntegrationLazy from "src/pages/SettingsPage/components/LangSmithIntegration-lazy";
import LinearIntegrationLazy from "src/pages/SettingsPage/components/LinearIntegration-lazy";
import NewRelicIntegration from "src/pages/SettingsPage/components/NewRelicIntegration";
import NotionIntegrationLazy from "src/pages/SettingsPage/components/NotionIntegration-lazy";
import OpsgenieIntegrationLazy from "src/pages/SettingsPage/components/OpsgenieIntegration-lazy";
import PagerDutyIntegrationLazy from "src/pages/SettingsPage/components/PagerDutyIntegration-lazy";
import PGAnalyzeIntegration from "src/pages/SettingsPage/components/PGAnalyzeIntegration";
import PostgreSQLIntegrationLazy from "src/pages/SettingsPage/components/PostgreSQLIntegration-lazy";
import PrometheusIntegrationLazy from "src/pages/SettingsPage/components/PrometheusIntegration-lazy";
import RollbarIntegrationLazy from "src/pages/SettingsPage/components/RollbarIntegration-lazy";
import RollbarIntegrationsDialog from "src/pages/SettingsPage/components/RollbarIntegrationsDialog";
import RootlyIntegrationLazy from "src/pages/SettingsPage/components/RootlyIntegration-lazy";
import SentryIntegrationLazy from "src/pages/SettingsPage/components/SentryIntegration-lazy";
import SlackIntegration from "src/pages/SettingsPage/components/SlackIntegration";
import SnowflakeIntegrationLazy from "src/pages/SettingsPage/components/SnowflakeIntegration-lazy";
import SocketIntegrationLazy from "src/pages/SettingsPage/components/SocketIntegration-lazy";
import TemporalIntegration from "src/pages/SettingsPage/components/TemporalIntegration";
import { IntegrationProvider } from "src/pages/SettingsPage/context/IntegrationContext";
import { getProductName, getSupportEmail } from "src/utils/brandTheme";

import { IntegrationCategoryContent_organization$key } from "./__generated__/IntegrationCategoryContent_organization.graphql";
import McpServersOnboardingContent from "./McpServersOnboardingContent";
import { OnboardingWizard } from "./OnboardingWizard";

const integrationCategoryContentFragment = graphql`
  fragment IntegrationCategoryContent_organization on GraphQLOrganization {
    brandTheme
    slackbotInviteLink
    integrations {
      edges {
        node {
          id
          integrationType
        }
      }
    }
  }
`;

interface IntegrationCategoryContentProps {
  category: IntegrationCategory;
  organization: IntegrationCategoryContent_organization$key;
  onRefetch: () => void;
}

export default function IntegrationCategoryContent({
  category,
  organization: organizationKey,
  onRefetch,
}: IntegrationCategoryContentProps) {
  const navigate = useNavigate();
  const { isSuperAdmin } = usePermissions();
  const organization = useFragment(integrationCategoryContentFragment, organizationKey);

  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationType | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const brandTheme = organization.brandTheme;
  const slackbotInviteLink = organization.slackbotInviteLink;

  // Get the dynamic category based on brand theme (for correct title and filtered integrations)
  const productName = getProductName(brandTheme);
  const dynamicCategories = getIntegrationCategories(productName, brandTheme, isSuperAdmin);
  const dynamicCategory = dynamicCategories.find(cat => cat.id === category.id);

  // If this category has no visible integrations for the current user, redirect back
  if (!dynamicCategory) {
    return <Navigate to="/onboarding" replace />;
  }

  // MCP servers use a dedicated inline UI with dialogs instead of integration cards
  if (category.id === "mcp-servers") {
    return (
      <OnboardingWizard currentStep={1} brandTheme={brandTheme}>
        <McpServersOnboardingContent onRefetch={onRefetch} />
      </OnboardingWizard>
    );
  }

  // Build a map of integration types to their IDs and a set of connected types
  const integrationMap = new Map<string, string>();
  (organization.integrations?.edges ?? []).forEach(edge => {
    if (edge?.node?.integrationType && edge?.node?.id) {
      integrationMap.set(edge.node.integrationType, edge.node.id);
    }
  });
  const connectedTypes = new Set(integrationMap.keys());

  // Helper to get integration object for generic dialog
  const getIntegrationForDialog = (type: IntegrationType) => ({
    id: integrationMap.get(type),
    name: INTEGRATION_INFO[type]?.name || type,
    type,
    isConnected: connectedTypes.has(type),
  });

  const handleBack = () => {
    navigate("/onboarding");
  };

  const handleIntegrationClick = (type: IntegrationType) => {
    setSelectedIntegration(type);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedIntegration(null);
  };

  const handleRefetch = () => {
    onRefetch();
    handleDialogClose();
  };

  const isConnected = (type: IntegrationType) => connectedTypes.has(type);
  const isAvailable = (type: IntegrationType) => INTEGRATION_INFO[type]?.available !== false;

  // Sort integrations: Connected (alpha) -> Not Connected (alpha) -> Not Available (alpha)
  const sortedIntegrationTypes = [...dynamicCategory.integrationTypes].sort((a, b) => {
    const aConnected = isConnected(a);
    const bConnected = isConnected(b);
    const aAvailable = isAvailable(a);
    const bAvailable = isAvailable(b);
    const aName = INTEGRATION_INFO[a]?.name || a;
    const bName = INTEGRATION_INFO[b]?.name || b;

    // Connected integrations come first
    if (aConnected && !bConnected) return -1;
    if (!aConnected && bConnected) return 1;

    // If both connected or both not connected, check availability
    if (!aConnected && !bConnected) {
      // Available (not connected) comes before unavailable
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;
    }

    // Within same category, sort alphabetically by name
    return aName.localeCompare(bName);
  });

  const renderIntegrationDialog = () => {
    if (!dialogOpen || !selectedIntegration) return null;

    const dialogProps = {
      isOpen: dialogOpen,
      onClose: handleDialogClose,
      onRefetch: handleRefetch,
      brandTheme,
    };

    const loadingFallback = (
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
          <DialogHeader>
            <DialogTitle>Loading...</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center p-8">
            <LoadingSpinner className="h-6 w-6" />
          </div>
        </DialogContent>
      </Dialog>
    );

    const integrationDialogs: Record<IntegrationType, React.ReactNode> = {
      DATADOG: isConnected("DATADOG")
        ? <DatadogIntegrationsDialog {...dialogProps} />
        : <DatadogIntegration {...dialogProps} />,
      NEW_RELIC: isConnected("NEW_RELIC")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("NEW_RELIC")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <NewRelicIntegration {...dialogProps} />,
      GRAFANA_CLOUD: isConnected("GRAFANA_CLOUD")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("GRAFANA_CLOUD")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <GrafanaCloudIntegration {...dialogProps} />,
      GCP_CLOUD_MONITORING: isConnected("GCP_CLOUD_MONITORING")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("GCP_CLOUD_MONITORING")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <GcpCloudMonitoringIntegration {...dialogProps} />,
      SENTRY: isConnected("SENTRY")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("SENTRY")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <SentryIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      ROLLBAR: isConnected("ROLLBAR")
        ? (
          <Suspense fallback={loadingFallback}>
            <RollbarIntegrationsDialog {...dialogProps} />
          </Suspense>
        )
        : (
          <Suspense fallback={loadingFallback}>
            <RollbarIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      ELASTICSEARCH: isConnected("ELASTICSEARCH")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("ELASTICSEARCH")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <ElasticsearchIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      LANGSMITH: isConnected("LANGSMITH")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("LANGSMITH")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <LangSmithIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      LANGFUSE: isConnected("LANGFUSE")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("LANGFUSE")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <LangfuseIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      CHRONOSPHERE: (
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="sm:max-w-md bg-popover text-foreground border-border">
            <DialogHeader>
              <DialogTitle>Chronosphere</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              The Chronosphere integration is in early preview. Please contact{" "}
              <a href={`mailto:${getSupportEmail(brandTheme)}`} className="text-primary hover:underline">
                {getSupportEmail(brandTheme)}
              </a>{" "}
              to get access.
            </p>
          </DialogContent>
        </Dialog>
      ),
      // Unavailable integrations - no dialogs needed
      APPDYNAMICS: null,
      CLOUDWATCH: isConnected("CLOUDWATCH")
        ? <CloudWatchIntegrationsDialogLazy {...dialogProps} />
        : <CloudWatchIntegrationLazy {...dialogProps} />,
      DYNATRACE: null,
      OPENSEARCH: null,
      PROMETHEUS: isConnected("PROMETHEUS")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("PROMETHEUS")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <PrometheusIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      SPLUNK: null,
      BITBUCKET: isConnected("BITBUCKET")
        ? <BitbucketIntegrationsDialog {...dialogProps} />
        : <BitbucketIntegration {...dialogProps} />,
      GITHUB: isConnected("GITHUB")
        ? <GitHubIntegrationsDialog {...dialogProps} />
        : <GithubIntegration {...dialogProps} source="onboarding" />,
      GITLAB: isConnected("GITLAB")
        ? <GitLabIntegrationsDialog {...dialogProps} />
        : <GitLabIntegration {...dialogProps} />,
      SLACK: <SlackIntegration {...dialogProps} slackbotInviteLink={slackbotInviteLink} isConnected={isConnected("SLACK")} />,
      DISCORD: (
        <Suspense fallback={loadingFallback}>
          <DiscordIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      PAGERDUTY: (
        <Suspense fallback={loadingFallback}>
          <PagerDutyIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      OPSGENIE: (
        <Suspense fallback={loadingFallback}>
          <OpsgenieIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      ROOTLY: (
        <Suspense fallback={loadingFallback}>
          <RootlyIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      FIREHYDRANT: (
        <Suspense fallback={loadingFallback}>
          <FireHydrantIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      INCIDENT_IO: <IncidentIoIntegration {...dialogProps} />,
      NOTION: isConnected("NOTION")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("NOTION")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <NotionIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      CONFLUENCE: isConnected("CONFLUENCE")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("CONFLUENCE")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <ConfluenceIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      JIRA: isConnected("JIRA")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("JIRA")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <JiraIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      LINEAR: isConnected("LINEAR")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("LINEAR")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <LinearIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      TEMPORAL: isConnected("TEMPORAL")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("TEMPORAL")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <TemporalIntegration {...dialogProps} />,
      BUILDKITE: isConnected("BUILDKITE")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("BUILDKITE")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <BuildkiteIntegration {...dialogProps} />,
      AWS: (
        <Suspense fallback={loadingFallback}>
          <AwsIntegrationsDialogLazy {...dialogProps} />
        </Suspense>
      ),
      GCP: (
        <Suspense fallback={loadingFallback}>
          <GcpIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      SNOWFLAKE: (
        <Suspense fallback={loadingFallback}>
          <SnowflakeIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      PGANALYZE: <PGAnalyzeIntegration {...dialogProps} />,
      SOCKET: <SocketIntegrationLazy {...dialogProps} />,
      POSTGRESQL: (
        <Suspense fallback={loadingFallback}>
          <PostgreSQLIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      CURSOR: <CursorIntegration {...dialogProps} />,
      DEVIN: <DevinIntegration {...dialogProps} />,
    };

    return (
      <IntegrationProvider>
        {integrationDialogs[selectedIntegration]}
      </IntegrationProvider>
    );
  };

  return (
    <OnboardingWizard currentStep={1} brandTheme={brandTheme}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Integrations
          </button>

          <h2 className="text-2xl font-semibold text-foreground">
            {dynamicCategory.title} Integrations
          </h2>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedIntegrationTypes.map(type => {
            // Hide manage button for Rootly integration in Rootly branded orgs
            const isRootlyBranded = productName === "Rootly AI SRE";
            const hideManage = isRootlyBranded && type === "ROOTLY";
            return (
              <IntegrationCard
                key={type}
                integrationType={type}
                isConnected={isConnected(type)}
                onClick={() => handleIntegrationClick(type)}
                hideManage={hideManage}
              />
            );
          })}
        </div>
      </div>

      {renderIntegrationDialog()}
    </OnboardingWizard>
  );
}
